import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { isNil } from 'lodash';
import { useState } from 'react';

import { getDomain } from '@/public/utils/url';
import { Tooltip } from '@/components/tooltip';

import { getFootnoteRefId } from './utils';
import { getFootnoteGroupInfo } from './grouping-utils';
import { GroupedCitationsPopover } from './grouped-citations-popover';

import styles from './footnote.module.scss';

export interface FootnoteAttributes {
  href: string;
  label?: string;
  citeId: string;
  num: number;
}

const GroupSize = 2;

export const Footnote = ({ node, editor, getPos }: NodeViewProps) => {
  const [tooltipKey, setTooltipKey] = useState(0);
  const attrs = node.attrs as FootnoteAttributes;
  const { citeId, href } = attrs;
  const domain = isNil(href) || href.length === 0 ? null : getDomain(href);

  if (domain === null) {
    return null;
  }

  const groupInfo = getFootnoteGroupInfo(editor, getPos);
  if (!groupInfo.isFirstInGroup && groupInfo.groupSize > GroupSize) {
    // Only render nodes that are either first in a group (custom label) or if there are less than 3 consecutive footnotes in a row
    return null;
  }

  const groupLabel =
    groupInfo.groupSize > GroupSize
      ? `${groupInfo.groupSize} source${groupInfo.groupSize > 1 ? 's' : ''}`
      : String(attrs.num);

  const handleClose = () => {
    // Forces a render, closing the popover
    setTooltipKey((prev) => prev + 1);
  };

  return (
    <NodeViewWrapper className={styles.footnote}>
      <Tooltip
        key={tooltipKey}
        content={
          <GroupedCitationsPopover
            groupCiteIds={groupInfo.groupCiteIds}
            editor={editor}
            onClose={handleClose}
          />
        }
        trigger="click"
        placement="bottom-start"
        hasArrow={false}>
        <button
          id={`${getFootnoteRefId(citeId)}`}
          aria-describedby="footnote-label"
          aria-label={groupLabel}
          data-label={groupLabel}
          className={groupInfo.groupSize > GroupSize ? styles.footnoteGroup : styles.footnoteRef}
          type="button"
        />
      </Tooltip>
    </NodeViewWrapper>
  );
};
